import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Trash2, Link2, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export const Route = createFileRoute("/_authenticated/admin/links")({
  component: AdminLinks,
});

interface Profile {
  id: string;
  full_name: string | null;
}
interface Role {
  user_id: string;
  role: string;
}
interface PCLink {
  id: string;
  parent_id: string;
  student_id: string;
}
interface Group {
  id: string;
  name: string;
}
interface GMLink {
  id: string;
  group_id: string;
  student_id: string;
}

function AdminLinks() {
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState<Map<string, Profile>>(new Map());
  const [roles, setRoles] = useState<Role[]>([]);
  const [pc, setPc] = useState<PCLink[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [gm, setGm] = useState<GMLink[]>([]);

  const load = async () => {
    const [p, r, l, g, m] = await Promise.all([
      supabase.from("profiles").select("id,full_name"),
      supabase.from("user_roles").select("user_id,role"),
      supabase.from("parent_children").select("*"),
      supabase.from("groups").select("id,name").order("name"),
      supabase.from("group_members").select("*"),
    ]);
    const pmap = new Map<string, Profile>();
    (p.data ?? []).forEach((row) =>
      pmap.set(row.id as string, {
        id: row.id as string,
        full_name: row.full_name as string | null,
      }),
    );
    setProfiles(pmap);
    setRoles((r.data ?? []) as Role[]);
    setPc((l.data ?? []) as PCLink[]);
    setGroups((g.data ?? []) as Group[]);
    setGm((m.data ?? []) as GMLink[]);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const parents = roles.filter((r) => r.role === "parent").map((r) => r.user_id);
  const students = roles.filter((r) => r.role === "student").map((r) => r.user_id);
  const nameOf = (id: string) => profiles.get(id)?.full_name || id.slice(0, 8);

  const linkParent = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const { error } = await supabase.from("parent_children").insert({
      parent_id: String(fd.get("parent_id")),
      student_id: String(fd.get("student_id")),
    });
    if (error) return toast.error(error.message);
    toast.success("Linked");
    load();
  };
  const unlinkParent = async (id: string) => {
    await supabase.from("parent_children").delete().eq("id", id);
    load();
  };

  const addToGroup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const { error } = await supabase.from("group_members").insert({
      group_id: String(fd.get("group_id")),
      student_id: String(fd.get("student_id")),
    });
    if (error) return toast.error(error.message);
    toast.success("Added");
    load();
  };
  const removeFromGroup = async (id: string) => {
    await supabase.from("group_members").delete().eq("id", id);
    load();
  };

  if (loading)
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );

  return (
    <div>
      <h1 className="text-3xl font-extrabold mb-6">Links & Membership</h1>
      <Tabs defaultValue="pc">
        <TabsList className="rounded-full">
          <TabsTrigger value="pc" className="rounded-full">
            <Link2 className="h-4 w-4 me-2" />
            Parent ↔ Child
          </TabsTrigger>
          <TabsTrigger value="gm" className="rounded-full">
            <UsersIcon className="h-4 w-4 me-2" />
            Group Members
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pc" className="mt-6">
          <Card className="rounded-3xl border-0 shadow-soft mb-6">
            <CardContent className="p-6">
              <form onSubmit={linkParent} className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>Parent</Label>
                  <select
                    name="parent_id"
                    required
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">— اختر —</option>
                    {parents.map((id) => (
                      <option key={id} value={id}>
                        {nameOf(id)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label>Student</Label>
                  <select
                    name="student_id"
                    required
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">— اختر —</option>
                    {students.map((id) => (
                      <option key={id} value={id}>
                        {nameOf(id)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <Button
                    type="submit"
                    className="rounded-full bg-gradient-primary text-primary-foreground border-0 w-full"
                  >
                    <Plus className="h-4 w-4 me-2" />
                    Link
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
          <div className="space-y-2">
            {pc.map((l) => (
              <Card key={l.id} className="rounded-2xl border-0 shadow-soft">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <span className="font-bold">{nameOf(l.parent_id)}</span> →{" "}
                    <span className="font-bold">{nameOf(l.student_id)}</span>
                  </div>
                  <Button size="icon" variant="destructive" onClick={() => unlinkParent(l.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
            {!pc.length && <p className="text-muted-foreground text-center py-6">لا توجد روابط</p>}
          </div>
        </TabsContent>

        <TabsContent value="gm" className="mt-6">
          <Card className="rounded-3xl border-0 shadow-soft mb-6">
            <CardContent className="p-6">
              <form onSubmit={addToGroup} className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>Group</Label>
                  <select
                    name="group_id"
                    required
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">— اختر —</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label>Student</Label>
                  <select
                    name="student_id"
                    required
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">— اختر —</option>
                    {students.map((id) => (
                      <option key={id} value={id}>
                        {nameOf(id)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <Button
                    type="submit"
                    className="rounded-full bg-gradient-primary text-primary-foreground border-0 w-full"
                  >
                    <Plus className="h-4 w-4 me-2" />
                    Add
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
          <div className="space-y-2">
            {gm.map((l) => (
              <Card key={l.id} className="rounded-2xl border-0 shadow-soft">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <span className="font-bold">
                      {groups.find((g) => g.id === l.group_id)?.name || "?"}
                    </span>{" "}
                    ← <span className="font-bold">{nameOf(l.student_id)}</span>
                  </div>
                  <Button size="icon" variant="destructive" onClick={() => removeFromGroup(l.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
            {!gm.length && <p className="text-muted-foreground text-center py-6">لا يوجد أعضاء</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
